/**
 * InterruptiveAlertModal - Modal for critical/escalated alerts
 * 
 * Shows on login, refresh, or route change when critical alerts need immediate attention.
 * 
 * Features:
 * - Displays alert with full context and explanation
 * - "Why am I seeing this?" collapsible section
 * - Primary CTA button for action
 * - Secondary actions: Snooze, Dismiss, Acknowledge
 * - Session gating to prevent repeated pops
 * - Mobile-optimized
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  X, AlertTriangle, AlertCircle, ChevronDown, ChevronUp,
  Clock, CheckCircle, XCircle
} from 'lucide-react'
import type { Alert } from '../../hooks/useCoachAlerts'

export interface InterruptiveAlertModalProps {
  alert: Alert | null
  onDismiss: () => void
  onSnooze: (minutes: number) => void
  onAcknowledge: () => void
  onAction?: () => void
}

const severityConfig = {
  critical: {
    icon: AlertCircle,
    iconColor: 'text-bad',
    bgColor: 'bg-bad/10',
    borderColor: 'border-bad/30',
    btnClass: 'bg-bad hover:bg-bad text-on-brand',
  },
  warning: {
    icon: AlertTriangle,
    iconColor: 'text-warn',
    bgColor: 'bg-warn/10',
    borderColor: 'border-warn/30',
    btnClass: 'bg-warn hover:bg-warn text-on-brand',
  },
  nudge: {
    icon: AlertTriangle,
    iconColor: 'text-brand',
    bgColor: 'bg-brand-subtle',
    borderColor: 'border-brand-subtle',
    btnClass: 'bg-brand hover:bg-brand-hover text-on-brand',
  },
  info: {
    icon: AlertCircle,
    iconColor: 'text-muted',
    bgColor: 'bg-surface-alt',
    borderColor: 'border-soft',
    btnClass: 'bg-strong hover:bg-strong text-on-brand',
  },
}

export function InterruptiveAlertModal({
  alert,
  onDismiss,
  onSnooze,
  onAcknowledge,
  onAction,
}: InterruptiveAlertModalProps) {
  const navigate = useNavigate()
  const [showExplain, setShowExplain] = useState(false)
  const [showSnoozeOptions, setShowSnoozeOptions] = useState(false)

  if (!alert) {
    return null
  }

  const config = severityConfig[alert.severity] || severityConfig.info
  const Icon = config.icon

  const handleAction = () => {
    if (alert.action?.url) {
      if (onAction) {
        onAction()
      } else {
        navigate(alert.action.url)
      }
    }
    onDismiss()
  }

  const handleSnooze = (minutes: number) => {
    onSnooze(minutes)
    setShowSnoozeOptions(false)
  }

  const handleDismiss = () => {
    onDismiss()
  }

  const handleAcknowledge = () => {
    onAcknowledge()
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={handleDismiss}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className={`
            pointer-events-auto
            w-full max-w-lg
            bg-surface
            rounded-lg shadow-xl
            border ${config.borderColor}
            max-h-[90vh] overflow-y-auto
            animate-in fade-in zoom-in-95 duration-200
          `}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`p-4 border-b ${config.borderColor} ${config.bgColor}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <Icon className={`w-6 h-6 mt-0.5 ${config.iconColor} flex-shrink-0`} />
                <div>
                  <h3 className="text-lg font-semibold text-primary">
                    {alert.title}
                  </h3>
                  <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded mt-1 ${config.iconColor} bg-surface/50`}>
                    {alert.severity.toUpperCase()}
                  </span>
                </div>
              </div>
              <button
                onClick={handleDismiss}
                className="text-muted hover:text-secondary transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Message */}
          <div className="p-6">
            <p className="text-secondary text-base leading-relaxed whitespace-pre-wrap">
              {alert.message}
            </p>

            {/* "Why am I seeing this?" Collapsible */}
            {alert.explain && alert.explain.summary && (
              <div className="mt-4 border border-soft rounded-lg overflow-hidden">
                <button
                  onClick={() => setShowExplain(!showExplain)}
                  className="w-full px-4 py-3 flex items-center justify-between bg-surface-alt hover:bg-surface-alt transition-colors"
                >
                  <span className="text-sm font-medium text-secondary">
                    Why am I seeing this?
                  </span>
                  {showExplain ? (
                    <ChevronUp className="w-4 h-4 text-muted" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-muted" />
                  )}
                </button>
                
                {showExplain && (
                  <div className="px-4 py-3 bg-surface border-t border-soft">
                    <p className="text-sm text-secondary mb-2">
                      {alert.explain.summary}
                    </p>
                    {alert.explain.bullets && alert.explain.bullets.length > 0 && (
                      <ul className="space-y-1.5 text-sm text-secondary">
                        {alert.explain.bullets.map((bullet, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-muted mt-0.5">•</span>
                            <span>{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="px-6 pb-6 space-y-3">
            {/* Primary CTA */}
            {alert.action && (
              <button
                onClick={handleAction}
                className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${config.btnClass}`}
              >
                {alert.action.label}
              </button>
            )}

            {/* Acknowledge (if required) */}
            {alert.requires_ack && (
              <button
                onClick={handleAcknowledge}
                className="w-full py-3 px-4 rounded-lg font-medium bg-ok hover:bg-ok text-on-brand transition-colors flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-4 h-4" />
                I understand and acknowledge
              </button>
            )}

            {/* Secondary Actions Row */}
            <div className="flex gap-2">
              {/* Snooze */}
              <div className="relative flex-1">
                <button
                  onClick={() => setShowSnoozeOptions(!showSnoozeOptions)}
                  className="w-full py-2 px-3 rounded-lg font-medium bg-surface-alt hover:bg-surface-alt text-secondary transition-colors flex items-center justify-center gap-2"
                >
                  <Clock className="w-4 h-4" />
                  Snooze
                </button>

                {/* Snooze Dropdown */}
                {showSnoozeOptions && (
                  <div className="absolute bottom-full left-0 right-0 mb-2 bg-surface border border-soft rounded-lg shadow-lg overflow-hidden z-10">
                    <button
                      onClick={() => handleSnooze(60)}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-surface-alt transition-colors"
                    >
                      1 hour
                    </button>
                    <button
                      onClick={() => handleSnooze(240)}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-surface-alt transition-colors"
                    >
                      4 hours
                    </button>
                    <button
                      onClick={() => {
                        // Tomorrow at 9am (rough calculation)
                        const now = new Date()
                        const tomorrow9am = new Date(now)
                        tomorrow9am.setDate(tomorrow9am.getDate() + 1)
                        tomorrow9am.setHours(9, 0, 0, 0)
                        const minutesUntil = Math.floor((tomorrow9am.getTime() - now.getTime()) / 60000)
                        handleSnooze(minutesUntil)
                      }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-surface-alt transition-colors"
                    >
                      Tomorrow morning
                    </button>
                  </div>
                )}
              </div>

              {/* Dismiss */}
              <button
                onClick={handleDismiss}
                className="flex-1 py-2 px-3 rounded-lg font-medium bg-surface-alt hover:bg-surface-alt text-secondary transition-colors flex items-center justify-center gap-2"
              >
                <XCircle className="w-4 h-4" />
                Dismiss
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

/**
 * Hook for programmatic control of InterruptiveAlertModal
 */
export function useInterruptiveAlertModal() {
  const [alert, setAlert] = useState<Alert | null>(null)

  const show = (alertToShow: Alert) => {
    setAlert(alertToShow)
  }

  const hide = () => {
    setAlert(null)
  }

  return {
    alert,
    show,
    hide,
    isOpen: alert !== null,
  }
}
